/**
 * Tiny analytics layer around Google's gtag.js (loaded in /public/index.html).
 *
 * Why a wrapper?
 * - Guards against `window.gtag` being undefined (ad-blockers, SSR, etc.).
 * - Centralises the Google Ads conversion ID + send_to label so we have one
 *   place to update if Google Ads issues new conversion labels.
 * - De-duplicates the "purchase" event per order so React StrictMode's
 *   double-mount in dev, or any accidental re-renders, can never fire two
 *   conversion hits for one order.
 *
 * If you create a custom conversion action in Google Ads, replace
 * `CONVERSION_LABEL` with the label Google gives you (looks like "abc123XYZ").
 * Until then we fire the conversion against the bare Conversion ID, which
 * Google still accepts — it just attributes to the default "Page view"
 * conversion action on the campaign.
 */

const CONVERSION_ID = "AW-18189488718";
const CONVERSION_LABEL = null; // optional — set when Google Ads gives you one
const FIRED_KEY_PREFIX = "aigl-conv-fired:";

function hasGtag() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

function alreadyFired(orderId) {
  if (!orderId) return false;
  try {
    return sessionStorage.getItem(FIRED_KEY_PREFIX + orderId) === "1";
  } catch {
    return false;
  }
}

function markFired(orderId) {
  if (!orderId) return;
  try {
    sessionStorage.setItem(FIRED_KEY_PREFIX + orderId, "1");
  } catch {
    // session storage unavailable — best-effort
  }
}

/**
 * Fire a Google Ads conversion + GA4 purchase event for a confirmed order.
 *
 * @param {object} order
 * @param {string} order.orderId - unique txn id (used for dedup + tx attribute)
 * @param {number|string} order.value - amount paid (number; backend stores numeric)
 * @param {string} order.currency - ISO 4217 (USD, EUR, etc.)
 * @param {string} [order.productId]
 * @param {string} [order.productName]
 */
export function trackPurchase({ orderId, value, currency, productId, productName }) {
  if (!hasGtag()) return false;
  if (alreadyFired(orderId)) return false;

  const numericValue =
    typeof value === "number" ? value : parseFloat(value || "0") || 0;
  const cur = currency || "USD";

  // 1) Google Ads conversion
  const sendTo = CONVERSION_LABEL
    ? `${CONVERSION_ID}/${CONVERSION_LABEL}`
    : CONVERSION_ID;
  window.gtag("event", "conversion", {
    send_to: sendTo,
    value: numericValue,
    currency: cur,
    transaction_id: orderId || "",
  });

  // 2) GA4-style purchase event (harmless if GA4 isn't connected; useful if
  // the same tag is later linked to a GA4 property — Google Ads can pull GA4
  // ecommerce conversions automatically).
  const item = {
    item_id: productId || "unknown",
    item_name: productName || "AIGL Plan",
    price: numericValue,
    quantity: 1,
  };
  window.gtag("event", "purchase", {
    transaction_id: orderId || "",
    value: numericValue,
    currency: cur,
    items: [item],
  });

  markFired(orderId);
  return true;
}
